import type {
  LocationQuery,
  LocationQueryRaw,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from 'vue-router'

export const GalleryRouteNames = {
  models: 'gallery-models',
  model: 'gallery-model',
  thread: 'gallery-thread',
  collections: 'gallery-collections',
  collection: 'gallery-collection',
  collectionSettings: 'gallery-collection-settings',
  collectionModel: 'gallery-collection-model',
} as const

export type GalleryRouteName =
  (typeof GalleryRouteNames)[keyof typeof GalleryRouteNames]

export type GalleryView =
  | { name: 'models' }
  | { name: 'model'; bundleId: string }
  | { name: 'thread'; threadId: string }
  | { name: 'collections' }
  | { name: 'collection'; collectionId: string }
  | { name: 'collection-settings'; collectionId: string }
  | { name: 'collection-model'; collectionId: string; bundleId: string }

const firstString = (value: unknown) => {
  if (typeof value === 'string') return value
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  return undefined
}

export const galleryRoutes = {
  models: (): RouteLocationRaw => ({ name: GalleryRouteNames.models }),
  model: (bundleId: string, query?: LocationQueryRaw): RouteLocationRaw => ({
    name: GalleryRouteNames.model,
    params: { bundleId },
    ...(query ? { query } : {}),
  }),
  thread: (threadId: string): RouteLocationRaw => ({
    name: GalleryRouteNames.thread,
    params: { threadId },
  }),
  collections: (): RouteLocationRaw => ({
    name: GalleryRouteNames.collections,
  }),
  collection: (collectionId: string): RouteLocationRaw => ({
    name: GalleryRouteNames.collection,
    params: { collectionId },
  }),
  collectionSettings: (collectionId: string): RouteLocationRaw => ({
    name: GalleryRouteNames.collectionSettings,
    params: { collectionId },
  }),
  collectionModel: (
    collectionId: string,
    bundleId: string,
  ): RouteLocationRaw => ({
    name: GalleryRouteNames.collectionModel,
    params: { collectionId, bundleId },
  }),
}

export const getGalleryView = (
  route: Pick<RouteLocationNormalizedLoaded, 'name' | 'params'>,
): GalleryView | null => {
  const bundleId = firstString(route.params.bundleId)
  const collectionId = firstString(route.params.collectionId)
  const threadId = firstString(route.params.threadId)

  switch (route.name) {
    case GalleryRouteNames.models:
      return { name: 'models' }
    case GalleryRouteNames.model:
      return bundleId ? { name: 'model', bundleId } : null
    case GalleryRouteNames.thread:
      return threadId ? { name: 'thread', threadId } : null
    case GalleryRouteNames.collections:
      return { name: 'collections' }
    case GalleryRouteNames.collection:
      return collectionId ? { name: 'collection', collectionId } : null
    case GalleryRouteNames.collectionSettings:
      return collectionId ? { name: 'collection-settings', collectionId } : null
    case GalleryRouteNames.collectionModel:
      return collectionId && bundleId
        ? { name: 'collection-model', collectionId, bundleId }
        : null
    default:
      return null
  }
}

export const getGalleryParentLocation = (
  view: GalleryView,
): RouteLocationRaw => {
  if (view.name === 'model' || view.name === 'thread')
    return galleryRoutes.models()
  if (view.name === 'collection-model') {
    return galleryRoutes.collection(view.collectionId)
  }
  if (view.name === 'collection-settings') {
    return galleryRoutes.collection(view.collectionId)
  }
  if (view.name === 'collection') return galleryRoutes.collections()
  return view.name === 'models'
    ? galleryRoutes.models()
    : galleryRoutes.collections()
}

export const getLegacyGalleryLocation = (
  query: LocationQuery,
): RouteLocationRaw => {
  const bundleId = firstString(query.bundleId)?.trim()
  const collectionId = firstString(query.collectionId)?.trim()
  const shortcode = firstString(query.shortcode)?.trim()

  if (bundleId) {
    return galleryRoutes.model(bundleId, shortcode ? { shortcode } : undefined)
  }
  if (collectionId) return galleryRoutes.collection(collectionId)
  return galleryRoutes.models()
}

export const getSafeLocalRedirect = (
  value: unknown,
  fallback: RouteLocationRaw = galleryRoutes.models(),
): RouteLocationRaw => {
  const redirect = firstString(value)?.trim()
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return fallback
  }
  return redirect
}
